import React, { useCallback } from "react"
import { VariableSizeList } from "react-window"
import { useWindowHeight } from "@react-hook/window-size"
import {
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  ListItemIcon,
} from "@material-ui/core"

import { LoadingList } from "../common/LoadingList"
import { useHeights } from "../common/HeightProvider"
import { CompletionIndicator } from "../common/CompletionIndicator"
import { SchemaField } from "../featuer-layer-settings/schema"

interface FeatureListProps {
  features?: __esri.Graphic[]
  displayField?: string
  fields: SchemaField[] | undefined
  onClick: (feature: __esri.Graphic) => void
}

/** Scoped @material-ui/core stylesheet */
const useStyles = makeStyles<Theme>(theme => ({
  listItemIcon: {
    minWidth: 18,
    marginRight: theme.spacing(1),
    position: "relative",
    top: -2,
  },
  listItemText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}))

export const FeatureList = React.memo<FeatureListProps>(
  ({ features, fields, displayField, onClick }) => {
    /** Accessors for react-window */
    const itemSize = useCallback(() => 48, [])
    const itemKey = useCallback(
      (index: number) =>
        (features && features[index].attributes.OBJECTID) ||
        (features && features[index].attributes.ObjectId) ||
        index,
      [features]
    )

    /** Get element heights for calculation. Useful for overflow: scroll */
    const [heights] = useHeights()
    const windowHeight = useWindowHeight()

    /** Get scoped CSS classes */
    const classes = useStyles()

    return features ? (
      <VariableSizeList
        height={windowHeight - heights.header - heights.sidebarHeader}
        width={375}
        itemSize={itemSize}
        itemKey={itemKey}
        itemCount={features.length}
        itemData={{ features, displayField }}
      >
        {({ index, style }) => (
          <ListItem
            button
            style={style}
            key={
              features[index].attributes.OBJECTID ||
              features[index].attributes.ObjectId
            }
            onClick={() => onClick(features[index])}
          >
            {fields && (
              <ListItemIcon className={classes.listItemIcon}>
                <CompletionIndicator
                  attributes={features[index].attributes}
                  fields={fields}
                  width={8}
                  height={8}
                />
              </ListItemIcon>
            )}
            <ListItemText
              className={classes.listItemText}
              primary={
                displayField
                  ? features[index].attributes[displayField]
                  : features[index].attributes.OBJECTID
              }
            />
          </ListItem>
        )}
      </VariableSizeList>
    ) : (
      <LoadingList nested={false} secondary={false} icon={false} />
    )
  }
)
